import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { classNames } from 'primereact/utils';

const ProfitDialog = ({ visible, data, onHide, onSave }) => {
    const { th } = useLanguage();

    const [submitted, setSubmitted] = useState(false);
    const [dateError, setDateError] = useState(false);

    const [formData, setFormData] = useState({
        id: null,
        startDate: null,
        endDate: null,
        commercialProfit: null
    });

    useEffect(() => {
        const next = data
            ? {
                  id: data.id ?? null,
                  startDate: data.startDate ? new Date(data.startDate) : null,
                  endDate: data.endDate ? new Date(data.endDate) : null,
                  commercialProfit: data.commercialProfit !== undefined && data.commercialProfit !== null ? Number(data.commercialProfit) : null
              }
            : { id: null, startDate: null, endDate: null, commercialProfit: null };

        if (next.startDate && next.endDate && next.endDate < next.startDate) {
            next.endDate = next.startDate;
        }

        setFormData(next);
        setSubmitted(false);
        setDateError(false);
    }, [data, visible]);

    const handleChange = (field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'startDate' && updated.endDate && value && updated.endDate < value) {
                updated.endDate = value;
            }
            if (field === 'startDate' || field === 'endDate') setDateError(false);
            return updated;
        });
    };

    const handleSubmit = () => {
        setSubmitted(true);
        if (!formData.startDate || formData.commercialProfit === null) return;
        if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
            setDateError(true);
            return;
        }
        onSave({ ...formData });
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    return (
        <Dialog
            header={formData?.id ? th('customerAddressBrand.tabs.profit.dialog.title.update') : th('customerAddressBrand.tabs.profit.dialog.title.new')}
            visible={visible}
            style={{ width: '45vw' }}
            modal
            onHide={onHide}
            className="p-fluid"
            footer={footer}
        >
            <div className="formgrid grid">
                <div className="field col-12 md:col-6">
                    <label htmlFor="startDate" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.profit.dialog.label.startDate')}
                    </label>
                    <Calendar
                        id="startDate"
                        value={formData.startDate}
                        onChange={(e) => handleChange('startDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.profit.dialog.placeholder.startDate')}
                        className={classNames({ 'p-invalid': submitted && !formData.startDate })}
                        inputClassName="w-full"
                    />
                    {submitted && !formData.startDate && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="endDate" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.profit.dialog.label.endDate')}
                    </label>
                    <Calendar
                        id="endDate"
                        value={formData.endDate}
                        onChange={(e) => handleChange('endDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.profit.dialog.placeholder.endDate')}
                        minDate={formData.startDate || null}
                        className={classNames({ 'p-invalid': dateError })}
                        inputClassName="w-full"
                    />
                    {dateError && <small className="p-error">{th('customerAddressBrand.tabs.profit.dialog.error.endDateBeforeStartDate')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="commercialProfit" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.profit.dialog.label.commercialProfit')}
                    </label>
                    <InputNumber
                        id="commercialProfit"
                        value={formData.commercialProfit}
                        onValueChange={(e) => handleChange('commercialProfit', e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={4}
                        placeholder={th('customerAddressBrand.tabs.profit.dialog.placeholder.commercialProfit')}
                        className={classNames({ 'p-invalid': submitted && formData.commercialProfit === null })}
                        inputClassName="w-full"
                    />
                    {submitted && formData.commercialProfit === null && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="col-12 md:col-6"></div>
            </div>
        </Dialog>
    );
};

export default ProfitDialog;
